import { JUMP_LOOK, destinationChapter } from '@continuum/show-core';
import type { Chapter, Phase } from '@continuum/show-core';
import { Screen } from '../ui/Shell.js';
import { mono } from '../ui/atoms.js';
import type { ScreenCopy } from '../ui/screenText.js';

const YEARS = { 1: '2026', 2: '2213', 3: '2398', 4: '2617', 5: '2791' } as const;

/**
 * The jump. Phones down: the jump is on the big screen, and on the captain's NEXT (2026-10-24: the phones used to ask
 * for four taps "to launch", which did nothing — the rule card says watch the big screen). Where the ship is going,
 * the wormhole, and that.
 */
export function Jump({ chapter, phase, s }: { chapter: Chapter; phase: Phase; s: ScreenCopy }) {
  // Which era we are arriving at depends on whether the jump has fired yet.
  const arriving = destinationChapter(chapter, phase);

  return (
    <Screen
      style={{ background: '#0d0a1c', color: JUMP_LOOK.fg, padding: 24 }}
      footer={
        <div style={{ minHeight: 56, display: 'grid', placeItems: 'center', fontFamily: mono, fontSize: 13, letterSpacing: 2, color: '#c0b4ff', textAlign: 'center' }}>
          {s.jumpWatch}
        </div>
      }
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(0deg,transparent 0 3px,rgba(160,140,255,.05) 3px 4px)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', fontFamily: mono, fontSize: 12, letterSpacing: 1.5, color: '#b3a6ff' }}>{s.getReady}</div>

      <div style={{ position: 'relative', marginTop: 14, textAlign: 'center' }}>
        <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2, color: '#a497e6' }}>{s.nextStop}</div>
        <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: 5, lineHeight: 1, marginTop: 4 }}>{YEARS[arriving]}</div>
        <div style={{ marginTop: 6, fontFamily: mono, fontSize: 12, letterSpacing: 0.5, color: '#a497e6' }}>
          {arriving === 1 ? s.firstStop : s.destination(arriving)}
        </div>
      </div>

      <Wormhole />
    </Screen>
  );
}

function Wormhole() {
  const streaks = [
    [0, '#c0b4ff', 0],
    [45, '#8ceede', 0.2],
    [90, '#c0b4ff', 0.4],
    [135, '#ff7ab8', 0.6],
    [180, '#c0b4ff', 0.8],
    [225, '#8ceede', 1],
    [270, '#ff7ab8', 1.2],
    [315, '#c0b4ff', 1.4],
  ] as const;

  return (
    // The rings grow past the circle; they are clipped here, so the page never gets wider than the phone.
    <div aria-hidden="true" style={{ position: 'relative', margin: '14px auto 0', width: 210, height: 210, flex: 'none', overflow: 'hidden', borderRadius: '50%' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background:
            'conic-gradient(from 0deg,rgba(160,140,255,.32),rgba(63,242,230,.06),rgba(255,79,216,.26),rgba(160,140,255,.05),rgba(160,140,255,.32))',
          animation: 'ccSwirl 3.2s linear infinite',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle,#05060a 0 17%,rgba(5,6,10,.55) 34%,transparent 62%)',
        }}
      />
      {[
        ['rgba(160,140,255,.7)', 0],
        ['rgba(63,242,230,.6)', 0.6],
        ['rgba(255,79,216,.55)', 1.2],
        ['rgba(255,255,255,.4)', 1.8],
      ].map(([color, delay], i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1.5px solid ${color}`,
            animation: `ccWorm 2.4s linear ${String(delay)}s infinite`,
          }}
        />
      ))}
      {streaks.map(([deg, color, delay]) => (
        <div key={deg} style={{ position: 'absolute', left: '50%', top: '50%', transform: `rotate(${deg}deg)` }}>
          <div
            style={{
              width: 60,
              height: 2,
              borderRadius: 1,
              background: `linear-gradient(90deg,transparent,${color})`,
              animation: `ccStreak 1.5s linear ${delay}s infinite`,
            }}
          />
        </div>
      ))}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 14,
          height: 14,
          margin: '-7px 0 0 -7px',
          borderRadius: '50%',
          background: '#05060a',
          boxShadow: '0 0 18px 4px rgba(63,242,230,.55)',
        }}
      />
    </div>
  );
}
